import { useMsal } from '@azure/msal-react';
import { Loading } from 'components/Loading';
import { PrincipalSelectionBar } from 'components/PrincipalSelectionBar';
import { SiteHeader } from 'components/SiteHeader';
import { useCurrentUser, usePrincipals } from 'hooks/queries';
import {
  getCurrentPrincipal,
  redirectToSelectedPrincipal,
} from 'lib/currentPrincipal';
import React, { useEffect, useRef } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Error404 } from '../../components/ErrorPages';
import { ScrollToTop } from '../../components/ScrollToTop';
import { SiteFooter } from '../../components/SiteFooter';
import { useLocalizationCookieHandler } from '../../hooks/useLocalizationCookieHandler';
import { usePrincipalFeatures } from '../../hooks/usePrincipalFeatures';
import { tracking } from '../../lib/tracking';
import { useWindowSize } from '../../shared/useWindowSize';
import { UserType } from '../../utils/types';
import { AcceptPoliciesModal } from './AcceptPoliciesModal';
import { findRouteDefintionByPathname, routes, SecondaryRoute } from './Routes';
import { trackUserLoggedIn } from '../../utils/principal-service/event-endpoints';
import { GlobalFilterProvider } from '../../context/GlobalFilterProvider';
import { NavigationProvider } from '../../context/NavigationProvider';

export const ContentController: React.FC = () => {
  const { user, role, userType, update } = useCurrentUser();
  const { data: principals } = usePrincipals();
  const { currentPrincipal } = getCurrentPrincipal(principals);
  const { instance } = useMsal();

  const localizationCookieHandler = useLocalizationCookieHandler();

  const calledOnce = useRef(false);
  const [windowWidth] = useWindowSize();

  const { data: principalFeatures } = usePrincipalFeatures(currentPrincipal.id);

  useEffect(() => {
    async function handleLogin() {
      const response = await instance.handleRedirectPromise();
      if (response) {
        trackUserLoggedIn(currentPrincipal.id);
      }
    }

    handleLogin();
  }, [currentPrincipal.id, instance]);

  useEffect(() => {
    if (!calledOnce.current && windowWidth !== 0) {
      tracking.push('user', 'User Login', {
        loginStatus: 'logged in',
        type: role,
        principalID: currentPrincipal.id,
      });

      const {
        additionalInformation: { trackingCategory = '', trackingType = '' },
        name = '',
      } = findRouteDefintionByPathname(
        window.location.pathname,
        currentPrincipal.slug,
      );

      tracking.push('content', 'Content View', {
        category: trackingCategory,
        type: trackingType,
        content: name,
      });

      calledOnce.current = true;
    }
  }, [calledOnce, user, windowWidth, role, currentPrincipal, userType]);

  useEffect(() => {
    if (!principals) return;
    redirectToSelectedPrincipal(
      user.userId,
      principals,
      user.roles?.[currentPrincipal.id],
    );
  }, [currentPrincipal, principals, user]);

  useEffect(() => {
    localizationCookieHandler.changeAppLanguage();
  }, [localizationCookieHandler]);

  if (window.location.pathname === '/')
    return (
      <Loading className="flex w-full items-center justify-center" loading />
    );

  const hideHeader = userType === UserType.EXTERNAL;

  return (
    <>
      <ScrollToTop />
      <AcceptPoliciesModal
        isHidden={!!user.acceptedTermsOfUse}
        onConfirm={() => update({ acceptedTermsOfUse: new Date() })}
        onCancel={() => {
          instance.logoutRedirect({ onRedirectNavigate: () => false });
        }}
      />
      <div className="grow flex-col">
        <div className="flex h-screen w-full flex-col lg:flex-row">
          {!hideHeader && (
            <div className="left-0 top-0 block lg:hidden">
              <PrincipalSelectionBar />
            </div>
          )}
          <NavigationProvider>
            <SiteHeader />

            <div
              id="main-content"
              className="flex grow flex-col md:overflow-y-auto"
            >
              {!hideHeader && (
                <div className=" left-0 top-0 hidden lg:block">
                  <PrincipalSelectionBar />
                </div>
              )}
              <div className="relative flex shrink-0 grow flex-col">
                <GlobalFilterProvider>
                  <Routes>
                    {routes(currentPrincipal.slug)
                      .routes.filter((route) =>
                        principalFeatures?.find(
                          (feature) =>
                            feature.id === route.routeId ||
                            feature.secondaryFeatures?.find(
                              (secondaryFeature) =>
                                secondaryFeature.id === route.routeId,
                            ),
                        ),
                      )
                      .map(({ url, Component, routeId, secondaryRoutes }) => {
                        const primaryFeature = principalFeatures?.find(
                          (feature) => feature.id === routeId,
                        );

                        // sort features based on visibility order and extract the id
                        const sortedFeatureIds =
                          primaryFeature?.secondaryFeatures
                            ?.sort(
                              (a, b) => a.visibilityOrder - b.visibilityOrder,
                            )
                            ?.map((feature) => feature.id);

                        //  map and safely typeguard the filtered sorted routes
                        const filteredSecondaryRoutes = sortedFeatureIds
                          ?.map((id) =>
                            secondaryRoutes?.find(
                              (route) => route.routeId === id,
                            ),
                          )
                          .filter((route): route is SecondaryRoute => !!route);

                        return (
                          <Route
                            key={url}
                            path={url}
                            element={
                              <div className="main-page flex grow overflow-x-hidden">
                                <Component />
                              </div>
                            }
                          >
                            {filteredSecondaryRoutes &&
                              filteredSecondaryRoutes.length > 0 && (
                                <Route
                                  index
                                  element={
                                    <Navigate
                                      to={`/${currentPrincipal.slug}/${routeId}/${filteredSecondaryRoutes[0].routeId}`}
                                      replace
                                    />
                                  }
                                />
                              )}

                            {filteredSecondaryRoutes?.map((secondaryRoute) => {
                              return (
                                <Route
                                  key={secondaryRoute.routeId}
                                  path={secondaryRoute.routeId}
                                  element={<secondaryRoute.component />}
                                />
                              );
                            })}
                          </Route>
                        );
                      })}
                    <Route path="*" element={<Error404 />} />
                  </Routes>
                </GlobalFilterProvider>
              </div>
              <SiteFooter />
            </div>
          </NavigationProvider>
        </div>
      </div>
    </>
  );
};
